import type {
  TransactionAction,
  TransactionListParams,
  TransactionStatus,
} from "../types/api";

const MIN_PAGE = 1;
const MIN_LIMIT = 1;
const MAX_LIMIT = 100;
const DEFAULT_LIMIT = 20;

const API_ACTIONS = new Set<string>([
  "buy",
  "sell",
  "deposit",
  "withdraw",
  "send",
  "receive",
]);

const API_STATUSES = new Set<string>([
  "pending",
  "processing",
  "completed",
  "failed",
  "cancelled",
]);

export interface TransactionHistoryUiFilters {
  page?: number;
  limit?: number;
  accountId?: string;
  businessId?: string;
  asset?: string;
  filterType?: string;
  filterStatus?: string;
  sortBy?: string;
  startDate?: string;
  endDate?: string;
  type?: string;
}

export function clampTransactionPage(page?: number): number {
  if (page === undefined || page === null || Number.isNaN(Number(page))) {
    return MIN_PAGE;
  }
  return Math.max(MIN_PAGE, Math.floor(Number(page)));
}

export function clampTransactionLimit(limit?: number): number {
  if (limit === undefined || limit === null || Number.isNaN(Number(limit))) {
    return DEFAULT_LIMIT;
  }
  return Math.min(MAX_LIMIT, Math.max(MIN_LIMIT, Math.floor(Number(limit))));
}

export function mapUiStatusToApi(
  status?: string
): TransactionStatus | undefined {
  if (!status || status === "all") {
    return undefined;
  }

  const normalized = status.trim().toLowerCase();
  if (normalized === "complete" || normalized === "completed") {
    return "completed";
  }
  if (API_STATUSES.has(normalized)) {
    return normalized as TransactionStatus;
  }

  return undefined;
}

export function mapUiActionToApi(
  filterType?: string
): TransactionAction | undefined {
  if (!filterType || filterType === "all") {
    return undefined;
  }

  const normalized = filterType.trim().toLowerCase();
  if (normalized === "withdrawal") {
    return "withdraw";
  }
  if (normalized === "incoming") {
    return "receive";
  }
  if (normalized === "outgoing") {
    return "send";
  }
  if (API_ACTIONS.has(normalized)) {
    return normalized as TransactionAction;
  }

  return undefined;
}

export function mapUiSortToApi(
  sortBy?: string
): Pick<TransactionListParams, "sortBy" | "sortOrder"> {
  switch (sortBy) {
    case "date-asc":
      return { sortBy: "createdAt", sortOrder: "asc" };
    case "amount-desc":
      return { sortBy: "amount", sortOrder: "desc" };
    case "amount-asc":
      return { sortBy: "amount", sortOrder: "asc" };
    case "date-desc":
    default:
      return { sortBy: "createdAt", sortOrder: "desc" };
  }
}

export function requiresClientStatusFilter(filterStatus?: string): boolean {
  const normalized = (filterStatus || "").trim().toLowerCase();
  return normalized === "awaiting settlement";
}

export function mapTransactionHistoryUiToListParams(
  ui: TransactionHistoryUiFilters
): TransactionListParams {
  const params: TransactionListParams = {
    page: clampTransactionPage(ui.page),
    limit: clampTransactionLimit(ui.limit),
    ...mapUiSortToApi(ui.sortBy),
  };

  const accountId = ui.accountId?.trim();
  if (accountId) {
    params.accountId = accountId;
  }

  const businessId = ui.businessId?.trim();
  if (businessId) {
    params.businessId = businessId;
  }

  const asset = ui.asset?.trim();
  if (asset) {
    params.asset = asset;
  }

  const type = ui.type?.trim();
  if (type) {
    params.type = type;
  }

  const action = mapUiActionToApi(ui.filterType);
  if (action) {
    params.action = action;
  }

  const status = mapUiStatusToApi(ui.filterStatus);
  if (status) {
    params.status = status;
  }

  const startDate = ui.startDate?.trim() || undefined;
  const endDate = ui.endDate?.trim() || undefined;
  if (startDate) {
    params.startDate = startDate;
  }
  if (endDate) {
    params.endDate = endDate;
  }

  return params;
}

export function buildTransactionListQueryParams(
  params?: TransactionListParams
): Record<string, string> {
  if (!params) {
    return {};
  }

  const query: Record<string, string> = {};
  const page = clampTransactionPage(params.page);
  const limit = clampTransactionLimit(params.limit);

  query.page = String(page);
  query.limit = String(limit);

  if (params.accountId?.trim()) {
    query.accountId = params.accountId.trim();
  }
  if (params.businessId?.trim()) {
    query.businessId = params.businessId.trim();
  }
  if (params.type?.trim()) {
    query.type = params.type.trim();
  }
  if (params.action?.trim()) {
    query.action = params.action.trim();
  }
  if (params.status) {
    query.status = params.status;
  }
  if (params.asset?.trim()) {
    query.asset = params.asset.trim();
  }
  if (params.sortBy) {
    query.sortBy = params.sortBy;
  }
  if (params.sortOrder) {
    query.sortOrder = params.sortOrder;
  }

  const startDate = params.startDate?.trim() || params.from?.trim();
  const endDate = params.endDate?.trim() || params.to?.trim();
  if (startDate) {
    query.startDate = startDate;
  }
  if (endDate) {
    query.endDate = endDate;
  }

  if (params.activity) {
    query.activity = params.activity;
  }

  return query;
}
